import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiBriefcase,
  FiShoppingCart,
  FiClock,
  FiMessageSquare,
  FiTrendingUp,
  FiArrowRight,
  FiDollarSign,
  FiAlertTriangle,
  FiGrid,
  FiLayers,
  FiArchive,
  FiFileText,
  FiImage,
} from 'react-icons/fi';
import '../../styles/AdminTable.css';
import './Dashboard.css';

const API = process.env.REACT_APP_API_URL;

const fetchSafe = (url, authorization) =>
  axios
    .get(url, authorization ? { headers: { Authorization: `Bearer ${authorization}` } } : undefined)
    .then((res) => res.data)
    .catch(() => []);

const last14Days = () => {
  const days = [];
  for (let i = 13; i >= 0; i -= 1) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    days.push(d);
  }
  return days;
};

const AreaChart = ({ points }) => {
  const width = 640;
  const height = 160;
  const max = Math.max(1, ...points.map((p) => p.value));
  const stepX = width / Math.max(1, points.length - 1);

  const coords = points.map((p, i) => ({
    x: i * stepX,
    y: height - (p.value / max) * (height - 24) - 8,
  }));

  const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="hd-admin-chart-svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="hdAdminChartGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b8bff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#3b8bff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#hdAdminChartGrad)" />
      <path d={linePath} fill="none" stroke="#3b8bff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r="3" fill="#3b8bff" />
      ))}
    </svg>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [connectedUser, setConnectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    bureau: [], table: [], chaise: [], armoire: [], comptoir: [], commandes: [], reclamations: [],
  });

  useEffect(() => {
    const raw = localStorage.getItem('admin_data');
    if (raw) setConnectedUser(JSON.parse(raw));
  }, []);

  useEffect(() => {
    const authorization = localStorage.getItem('token');
    Promise.all([
      fetchSafe(`${API}/bureau/lister`),
      fetchSafe(`${API}/table/lister`),
      fetchSafe(`${API}/chaise/lister`),
      fetchSafe(`${API}/armoire/lister`),
      fetchSafe(`${API}/comptoir/lister`),
      fetchSafe(`${API}/commande/lister`, authorization),
      fetchSafe(`${API}/contact/lister`, authorization),
    ]).then(([bureau, table, chaise, armoire, comptoir, commandes, reclamations]) => {
      setData({ bureau, table, chaise, armoire, comptoir, commandes, reclamations });
      setLoading(false);
    });
  }, []);

  const stats = useMemo(() => {
    const totalProduits = data.bureau.length + data.table.length + data.chaise.length + data.armoire.length + data.comptoir.length;
    const commandesEnAttente = data.commandes.filter((c) => (c.statut || 'En attente') === 'En attente').length;
    const reclamationsNonLues = data.reclamations.filter((r) => !r.lu).length;
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const commandesSemaine = data.commandes.filter((c) => new Date(c.createdAt) >= weekAgo).length;
    const chiffreAffaires = data.commandes
      .filter((c) => c.statut !== 'Annulée')
      .reduce((total, c) => total + (c.article || []).reduce((s, a) => s + (a.price || 0) * (a.quantity || 1), 0), 0);
    return { totalProduits, commandesEnAttente, reclamationsNonLues, commandesSemaine, chiffreAffaires };
  }, [data]);

  const categoryBreakdown = useMemo(() => {
    const rows = [
      { label: 'Bureau', count: data.bureau.length, path: '/admin/Bureaux' },
      { label: 'Table', count: data.table.length, path: '/admin/Table' },
      { label: 'Chaise', count: data.chaise.length, path: '/admin/Chaise' },
      { label: 'Armoire', count: data.armoire.length, path: '/admin/Armoire' },
      { label: 'Comptoir', count: data.comptoir.length, path: '/admin/Comptoir' },
    ];
    const max = Math.max(1, ...rows.map((r) => r.count));
    return rows.map((r) => ({ ...r, pct: Math.round((r.count / max) * 100) }));
  }, [data]);

  const outOfStock = useMemo(() => {
    const sources = [
      { items: data.bureau, label: 'Bureau', path: '/admin/Bureaux' },
      { items: data.table, label: 'Table', path: '/admin/Table' },
      { items: data.chaise, label: 'Chaise', path: '/admin/Chaise' },
      { items: data.armoire, label: 'Armoire', path: '/admin/Armoire' },
      { items: data.comptoir, label: 'Comptoir', path: '/admin/Comptoir' },
    ];
    return sources.flatMap(({ items, label, path }) =>
      items.filter((p) => p.disponibilite === 'Hors stock').map((p) => ({ ...p, categorie: label, path }))
    );
  }, [data]);

  const chartPoints = useMemo(() => {
    const days = last14Days();
    return days.map((day) => {
      const next = new Date(day);
      next.setDate(next.getDate() + 1);
      const value = data.commandes.filter((c) => {
        const created = new Date(c.createdAt);
        return created >= day && created < next;
      }).length;
      return { label: day.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }), value };
    });
  }, [data.commandes]);

  const recentCommandes = useMemo(
    () => [...data.commandes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5),
    [data.commandes]
  );

  const recentReclamations = useMemo(
    () => data.reclamations.filter((r) => !r.lu).slice(0, 5),
    [data.reclamations]
  );

  const statCards = [
    { label: 'Produits au catalogue', value: stats.totalProduits, icon: <FiBriefcase />, link: '/admin/Table' },
    { label: 'Chiffre d\'affaires', value: `${stats.chiffreAffaires} DT`, icon: <FiDollarSign />, link: '/admin/Commande' },
    { label: 'Commandes (7 derniers jours)', value: stats.commandesSemaine, icon: <FiTrendingUp />, link: '/admin/Commande' },
    { label: 'Commandes en attente', value: stats.commandesEnAttente, icon: <FiClock />, link: '/admin/Commande' },
    { label: 'Réclamations non lues', value: stats.reclamationsNonLues, icon: <FiMessageSquare />, link: '/admin/Reclamation' },
    { label: 'Produits en rupture', value: outOfStock.length, icon: <FiAlertTriangle />, link: '/admin/Table' },
  ];

  return (
    <div className="hd-admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <span className="hd-admin-dashboard-eyebrow">Espace administration</span>
        <h1 className="hd-admin-dashboard-title">
          Bonjour{connectedUser?.prenom ? `, ${connectedUser.prenom}` : ''}
        </h1>
        <p className="hd-admin-dashboard-subtitle">Aperçu de l'activité de la boutique.</p>
      </motion.div>

      <div className="hd-admin-stat-grid">
        {statCards.map((card, index) => (
          <motion.button
            type="button"
            key={card.label}
            className="hd-admin-stat-card"
            onClick={() => navigate(card.link)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <span className="hd-admin-stat-icon">{card.icon}</span>
            <span className="hd-admin-stat-value">{loading ? '—' : card.value}</span>
            <span className="hd-admin-stat-label">{card.label}</span>
          </motion.button>
        ))}
      </div>

      <div className="hd-admin-dashboard-panels">
        <div className="hd-admin-panel hd-admin-panel-chart">
          <div className="hd-admin-panel-header">
            <h3>Commandes — 14 derniers jours</h3>
          </div>
          {loading ? (
            <div className="hd-admin-empty">Chargement...</div>
          ) : (
            <>
              <AreaChart points={chartPoints} />
              <div className="hd-admin-chart-labels">
                {chartPoints.filter((_, i) => i % 2 === 0).map((p) => (
                  <span key={p.label}>{p.label}</span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="hd-admin-panel">
          <div className="hd-admin-panel-header">
            <h3>Réclamations non lues</h3>
            <button type="button" className="hd-admin-panel-link" onClick={() => navigate('/admin/Reclamation')}>
              Voir tout <FiArrowRight />
            </button>
          </div>
          {recentReclamations.length === 0 ? (
            <div className="hd-admin-empty">Aucune réclamation non lue.</div>
          ) : (
            <ul className="hd-admin-mini-list">
              {recentReclamations.map((r) => (
                <li key={r._id}>
                  <span className="hd-admin-mini-list-title">{r.sujet}</span>
                  <span className="hd-admin-mini-list-sub">{r.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="hd-admin-dashboard-panels">
        <div className="hd-admin-panel hd-admin-panel-chart">
          <div className="hd-admin-panel-header">
            <h3>Répartition par catégorie</h3>
          </div>
          {loading ? (
            <div className="hd-admin-empty">Chargement...</div>
          ) : (
            <ul className="hd-admin-breakdown">
              {categoryBreakdown.map((c) => (
                <li key={c.label}>
                  <button type="button" className="hd-admin-breakdown-row" onClick={() => navigate(c.path)}>
                    <span>{c.label}</span>
                    <span>{c.count}</span>
                  </button>
                  <div className="hd-admin-breakdown-bar">
                    <div className="hd-admin-breakdown-bar-fill" style={{ width: `${c.pct}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="hd-admin-panel">
          <div className="hd-admin-panel-header">
            <h3>Produits en rupture</h3>
          </div>
          {outOfStock.length === 0 ? (
            <div className="hd-admin-empty">Aucun produit en rupture de stock.</div>
          ) : (
            <ul className="hd-admin-mini-list">
              {outOfStock.slice(0, 5).map((p) => (
                <li key={p._id} className="hd-admin-mini-list--clickable" onClick={() => navigate(p.path)}>
                  <span className="hd-admin-mini-list-title">{p.titre}</span>
                  <span className="hd-admin-mini-list-sub">{p.categorie} — Réf. {p.reference}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="hd-admin-panel">
        <div className="hd-admin-panel-header">
          <h3>Commandes récentes</h3>
          <button type="button" className="hd-admin-panel-link" onClick={() => navigate('/admin/Commande')}>
            Voir tout <FiArrowRight />
          </button>
        </div>
        {recentCommandes.length === 0 ? (
          <div className="hd-admin-empty">Aucune commande pour le moment.</div>
        ) : (
          <div className="hd-admin-table-wrap">
            <table className="hd-admin-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Téléphone</th>
                  <th>Statut</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentCommandes.map((c) => (
                  <tr key={c._id}>
                    <td>{c.prenom} {c.nom}</td>
                    <td>{c.numero}</td>
                    <td>{c.statut || 'En attente'}</td>
                    <td>{new Date(c.createdAt).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="hd-admin-dashboard-grid">
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Bureaux/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiBriefcase /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter un bureau</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Table/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiGrid /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter une table</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Chaise/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiLayers /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter une chaise</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Armoire/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiArchive /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter une armoire</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Comptoir/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiBriefcase /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter un comptoir</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Actualite/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiFileText /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter une actualité</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Carousel/ajouter')}>
          <span className="hd-admin-dashboard-card-icon"><FiImage /></span>
          <span className="hd-admin-dashboard-card-title">Ajouter au carousel</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
        <button type="button" className="hd-admin-dashboard-card" onClick={() => navigate('/admin/Commande')}>
          <span className="hd-admin-dashboard-card-icon"><FiShoppingCart /></span>
          <span className="hd-admin-dashboard-card-title">Gérer les commandes</span>
          <FiArrowRight className="hd-admin-dashboard-card-arrow" />
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
